import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface VacuumsViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
  database?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const VacuumsView: React.FC<VacuumsViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  database,
}) => {
  const [loading, setLoading] = useState(true);
  const [vacuumData, setVacuumData] = useState<any>(null);

  // Legend Hover States
  const [hoveredLive, setHoveredLive] = useState<string | null>(null);
  const [hoveredDead, setHoveredDead] = useState<string | null>(null);
  const [hoveredAutovac, setHoveredAutovac] = useState<string | null>(null);
  const [hoveredAutoanalyze, setHoveredAutoanalyze] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '15');

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;
    if (database) params.database = database;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/per_db/vacuum', { params });
      setVacuumData(res.data?.data || res.data);
    } catch (err) {
      console.warn('VacuumsView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, database]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formatSeries = (labels: string[], databases: string[], seriesMap: any, metricKey: string) => {
    if (!labels || labels.length === 0 || !databases) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      databases.forEach((db) => {
        const val = seriesMap?.[`${db}:${metricKey}`]?.[idx];
        point[db] = val !== undefined && val !== null ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  };

  const liveFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'live_tuples');
  const deadFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'dead_tuples');
  const autovacAgeFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'last_autovacuum_age_sec');
  const autoanalyzeAgeFormatted = formatSeries(vacuumData?.labels || [], vacuumData?.databases || [], vacuumData?.series || {}, 'last_autoanalyze_age_sec');

  const colors = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#a855f7', // Purple
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#06b6d4', // Cyan
    '#ec4899', // Pink
    '#84cc16', // Lime
    '#6366f1', // Indigo
    '#f97316', // Orange
    '#14b8a6', // Teal
    '#eab308', // Yellow
    '#8b5cf6', // Violet
    '#22c55e', // Emerald
    '#e11d48', // Rose
  ];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    )
  }
  return (
    <div className="space-y-3 relative">
      <div>
        <h2 className="text-sm font-bold text-white flex items-center gap-2">
          <div>
            <Sparkles className="w-4 h-4 text-orange-500"  />
          </div>

          Vacuum & Table Maintenance Diagnostics
        </h2>
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Live Rows per DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(liveFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={liveFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredLive} onHoverSeries={setHoveredLive} />} />
                  {Object.keys(liveFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[i % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredLive, dbKey)}
                        strokeWidth={hoveredLive === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredLive, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No live rows telemetry available</span>
            )}
          </div>
        </div>

        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Dead Rows per DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(deadFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={deadFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredDead} onHoverSeries={setHoveredDead} />} />
                  {Object.keys(deadFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[(i + 3) % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredDead, dbKey)}
                        strokeWidth={hoveredDead === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredDead, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No dead rows telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Oldest Autovacuum Age by DB (seconds)</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(autovacAgeFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={autovacAgeFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredAutovac} onHoverSeries={setHoveredAutovac} />} />
                  {Object.keys(autovacAgeFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[i % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredAutovac, dbKey)}
                        strokeWidth={hoveredAutovac === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredAutovac, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No autovacuum age telemetry available</span>
            )}
          </div>
        </div>

        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Oldest Autoanalyze Age by DB (seconds)</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(autoanalyzeAgeFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={autoanalyzeAgeFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredAutoanalyze} onHoverSeries={setHoveredAutoanalyze} />} />
                  {Object.keys(autoanalyzeAgeFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => {
                    const color = colors[(i + 2) % colors.length];
                    return (
                      <Line
                        key={dbKey}
                        type="monotone"
                        dataKey={dbKey}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredAutoanalyze, dbKey)}
                        strokeWidth={hoveredAutoanalyze === dbKey ? 2.5 : 1.5}
                        dot={getDot(hoveredAutoanalyze, dbKey, color)}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No autoanalyze age telemetry available</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
